import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Input,
  Button,
  VStack,
  HStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Box,
  Text,
  Image,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Flex,
  Icon,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText
} from '@chakra-ui/react';
import { FiSave, FiFile, FiImage, FiVideo, FiFileText, FiDownload, FiExternalLink } from 'react-icons/fi';
import { ArrowBackIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const EditMedia = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0] || null;

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  const [asset, setAsset] = useState({
    file_name: '',
    file_path: '',
    file_type: '',
    file_size: 0,
    created_at: '',
    updated_at: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalName, setOriginalName] = useState('');

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        const data = await apiClient(`/assets/${tenantId}/${id}`);
        setAsset(data);
        setOriginalName(data.file_name);
      } catch (error) {
        toast({
          title: 'Error fetching asset',
          description: error.message,
          status: 'error',
          duration: 3000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [tenantId, id, toast]);

  const handleChange = (e) => {
    setAsset({ ...asset, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      await apiClient(`/assets/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(asset)
      });

      toast({
        title: 'Success',
        description: 'Media updated successfully',
        status: 'success',
        duration: 3000
      });

      navigate('/media');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update asset',
        status: 'error',
        duration: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return FiImage;
    if (fileType.startsWith('video/')) return FiVideo;
    if (fileType === 'application/pdf') return FiFileText;
    return FiFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileUrl = () => {
    return `${process.env.REACT_APP_API_BASE_URL}${asset.file_path}`;
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={6}>
        <VStack align="center" justify="center" height="300px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading media...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/media">Media</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit {originalName}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Edit Media</Heading>
        <HStack spacing={4}>
          <Button 
            as={RouterLink}
            to="/media"
            leftIcon={<ArrowBackIcon />}
            variant="outline"
          >
            Back to Media
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Media Preview */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
            <Heading size="sm">Preview</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              {asset.file_type.startsWith('image/') ? (
                <Image
                  src={getFileUrl()}
                  alt={asset.file_name}
                  maxH="300px"
                  maxW="100%"
                  objectFit="contain"
                  borderRadius="md"
                  fallback={
                    <Flex
                      h="200px"
                      w="100%"
                      bg="gray.100"
                      borderRadius="md"
                      align="center"
                      justify="center"
                      direction="column"
                    >
                      <Icon as={FiImage} boxSize={12} color="gray.400" />
                      <Text color="gray.500" mt={2}>Image preview unavailable</Text>
                    </Flex>
                  }
                />
              ) : (
                <Flex
                  h="200px"
                  w="100%"
                  bg="gray.100"
                  borderRadius="md"
                  align="center"
                  justify="center"
                  direction="column"
                >
                  <Icon as={getFileIcon(asset.file_type)} boxSize={12} color="gray.400" />
                  <Text color="gray.500" mt={2}>{asset.file_type}</Text>
                </Flex>
              )}

              <HStack spacing={4} w="full">
                <Button
                  as="a"
                  href={getFileUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  leftIcon={<FiExternalLink />}
                  variant="outline"
                  size="sm"
                  flex={1}
                >
                  View Original
                </Button>
                <Button
                  as="a"
                  href={getFileUrl()}
                  download={asset.file_name}
                  leftIcon={<FiDownload />}
                  variant="outline"
                  size="sm"
                  flex={1}
                >
                  Download
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Edit Form */}
        <VStack spacing={6} align="stretch">
          {/* File Information */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
              <Heading size="sm">File Information</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>File Name</FormLabel>
                  <Input 
                    name="file_name" 
                    value={asset.file_name} 
                    onChange={handleChange}
                    placeholder="Enter file name"
                    focusBorderColor="blue.400"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>File Path</FormLabel>
                  <Input 
                    name="file_path" 
                    value={asset.file_path} 
                    onChange={handleChange} 
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>File Type</FormLabel>
                  <HStack>
                    <Input 
                      name="file_type" 
                      value={asset.file_type} 
                      onChange={handleChange} 
                      isReadOnly
                      bg="gray.50"
                      flex={1}
                    />
                    <Badge colorScheme="blue" px={3} py={1}>
                      {asset.file_type}
                    </Badge>
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* File Statistics */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
              <Heading size="sm">File Details</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={2} spacing={4}>
                <Stat>
                  <StatLabel>File Size</StatLabel>
                  <StatNumber fontSize="lg">
                    {formatFileSize(asset.file_size || 0)}
                  </StatNumber>
                </Stat>
                
                <Stat>
                  <StatLabel>File ID</StatLabel>
                  <StatNumber fontSize="lg">#{id}</StatNumber>
                </Stat>
                
                <Stat>
                  <StatLabel>Created</StatLabel>
                  <StatHelpText>{formatDate(asset.created_at)}</StatHelpText>
                </Stat>
                
                <Stat>
                  <StatLabel>Modified</StatLabel>
                  <StatHelpText>{formatDate(asset.updated_at)}</StatHelpText>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        </VStack>
      </SimpleGrid>

      {/* Action Buttons */}
      <Flex justify="flex-end" mt={8} gap={4} pt={6} borderTopWidth="1px" borderColor={borderColor}>
        <Button 
          variant="outline" 
          as={RouterLink}
          to="/media"
        >
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={saving}
          loadingText="Saving..."
          leftIcon={<FiSave />}
        >
          Save Changes
        </Button>
      </Flex>
    </Container>
  );
};

export default EditMedia;
